import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PROJECT_RISK_LABELS,
  PROJECT_STATUSES,
  PROJECT_STATUS_LABELS,
} from '@epasu/core/constants';
import type { ProjectStatus } from '@epasu/core/constants';
import { AlertTriangle } from 'lucide-react';
import { crm, type ProjectRow } from '@/lib/crm-client';
import { Card } from '@/components/ui/card';
import { ProjectProgress } from '@/components/ProjectRiskBand';
import { formatEuro } from '@/lib/utils';

/**
 * Projekte als Board über die feste Statusliste.
 *
 * Die Spalten sind die Status aus `api/constants.ts`, keine konfigurierbaren
 * Stufen: eine zweite Stufenmechanik neben den Vertriebs-Pipelines wäre ein
 * zweites Verfahren für dasselbe. Dieselbe Komponente trägt das Board über
 * alle Kunden und das im Projektreiter eines einzelnen.
 */
export function ProjectBoard({
  projects,
  onMoved,
  backTo,
}: {
  projects: ProjectRow[];
  onMoved: () => void;
  /** Herkunft für den Rücksprung aus der Projektakte — siehe deren Kopfzeile. */
  backTo: string;
}) {
  const navigate = useNavigate();
  const [gezogen, setGezogen] = useState<string | null>(null);
  const [ueber, setUeber] = useState<ProjectStatus | null>(null);
  // Fehler beim Ziehen, kein Dauerzustand — der naechste Versuch loescht ihn,
  // genau wie bei den Aktionen in der Projektakte (statusFehler dort).
  const [fehler, setFehler] = useState<string | null>(null);

  const ablegen = async (status: ProjectStatus) => {
    const id = gezogen;
    setGezogen(null);
    setUeber(null);
    if (!id) return;
    // Auf der eigenen Spalte abgelegt: das Backend noopt das ohnehin, aber
    // ohne diese Prüfung würde jedes Ablegen ein `onMoved()` auslösen und
    // damit die ganze Ansicht neu laden, nur um denselben Stand zu zeigen.
    const projekt = projects.find((p) => p.id === id);
    if (!projekt || projekt.status === status) return;
    setFehler(null);
    try {
      await crm.projects.setStatus(id, status);
      onMoved();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Status ändern fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-2">
      {fehler && <p className="text-sm text-danger">{fehler}</p>}
      <div className="flex gap-3 overflow-x-auto scrollbar-thin pb-2">
        {PROJECT_STATUSES.map((status) => {
          const spalte = projects.filter((p) => p.status === status);
          return (
            <div
              key={status}
              onDragOver={(e) => {
                e.preventDefault();
                setUeber(status);
              }}
              onDragLeave={() => setUeber((s) => (s === status ? null : s))}
              onDrop={() => void ablegen(status)}
              className={`w-64 shrink-0 rounded-lg bg-secondary/40 p-2 ${
                ueber === status ? 'ring-2 ring-ring' : ''
              }`}
            >
              <div className="mb-2 flex items-center justify-between px-1 text-sm font-semibold">
                <span>{PROJECT_STATUS_LABELS[status]}</span>
                <span className="text-muted-foreground">{spalte.length}</span>
              </div>

              <div className="space-y-2">
                {spalte.map((p) => (
                  <Card
                    key={p.id}
                    draggable
                    onDragStart={() => setGezogen(p.id)}
                    onDragEnd={() => setGezogen(null)}
                    onClick={() =>
                      navigate(`/projekte/${p.id}`, { state: { from: backTo } })
                    }
                    className="cursor-pointer space-y-1 p-3 text-sm hover:bg-accent/40"
                  >
                    <p className="font-medium leading-snug">{p.title}</p>
                    <p className="text-xs text-muted-foreground">{p.customerName}</p>
                    {p.phaseCount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {p.currentPhaseTitle
                          ? `Phase ${p.phasesDone + 1} von ${p.phaseCount} · ${p.currentPhaseTitle}`
                          : `Alle ${p.phaseCount} Phasen durch`}
                      </p>
                    )}
                    {p.summary && (
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {p.summary}
                      </p>
                    )}
                    <ProjectProgress progress={p.progress} className="pt-0.5" />
                    {p.risks.length > 0 && (
                      <p className="flex items-center gap-1 text-[11px] font-medium text-danger">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {PROJECT_RISK_LABELS[p.risks[0]!]}
                        {p.risks.length > 1 && ` +${p.risks.length - 1}`}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
                      {p.dueDate && (
                        <span>{new Date(p.dueDate).toLocaleDateString('de-DE')}</span>
                      )}
                      {p.openTasks > 0 && <span>{p.openTasks} offen</span>}
                      {p.orderValueCents > 0 && <span>{formatEuro(p.orderValueCents)}</span>}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
